// -----------------------------------------------------------------------------
// Anaglyphic, Netherlands @ 2023.
// See README in the root project for more information.
// -----------------------------------------------------------------------------

import baseRoute from "./baseRoute";
import { useBaseStore } from "@/stores/base";
import { createRouter, createWebHistory } from "vue-router";
import { RouteGuards } from "./guards";
import {
	clearRedirectAfterLogin,
	setRedirectAfterLogin,
} from "@/Utils/redirectAfterLogin";

// Router
// -----------------------------------------------------------------------------

export const router = createRouter({
	history: createWebHistory(),
	scrollBehavior: () => ({ top: 0, left: 0 }),
	routes: [baseRoute],
});

// -----------------------------------------------------------------------------

router.beforeEach(async (to, from, next) => {
	const store = useBaseStore();

	// Remember the deep link the user is after BEFORE dataInit runs: an expired
	// session makes appInit hard-redirect to /login (api/base.ts), wiping memory,
	// so this is the last point we still know the intended URL. sessionStorage
	// survives that reload; we hand the user back here after they log in.
	const isProtectedRoute =
		to.name !== "login" && to.meta.requiresAuth !== false;
	if (isProtectedRoute && !store.isAuthenticated) {
		setRedirectAfterLogin(to.fullPath);
	}

	if (!(await RouteGuards.dataInit({ to, store })) && to.name !== "login") {
		return next("/login");
	}

	if (!RouteGuards.hasPermissions({ to, store })) {
		console.warn("No permissions!");
		return next("/401");
	}
	if (!RouteGuards.isUserAuthenticated({ to, store })) {
		console.warn("Not Logged in!");
		return next("/login");
	}

	if (
		RouteGuards.isTwoFactorAuthenticationRequiredAndNotSetByUser({
			to,
			store,
		}) &&
		!to.name.startsWith("profile")
	) {
		console.warn("User should set 2FA in profile page");
		return next(`/profile/${store.currentUser.uuid}`);
	}

	if (RouteGuards.isUserAlreadyAuthed({ to, store })) {
		console.warn("User already logged in!");
		return next("/explorer");
	}

	// The user reached a protected route while authenticated, so no login
	// interception happened; drop any stale stored redirect.
	if (isProtectedRoute && store.isAuthenticated) {
		clearRedirectAfterLogin();
	}

	next();
});

export default router;
